/**
 * Denní aritmetika kalendáře. Celý layout pracuje s **číslem dne** (počet dní od 1. 1. 1970)
 * místo s `Date` — porovnání, rozsahy i šířky sloupců jsou pak celočíselné a nezávislé na
 * časové zóně i na letním čase.
 *
 * Číslo dne se počítá z **lokálních** složek data přes `Date.UTC`, takže půlnoc v Praze a
 * půlnoc v UTC dávají totéž číslo. Zpětný převod (`dateOfDay`) proto vrací `Date` v UTC —
 * čte se z něj jen `getUTC*`.
 */

const DAY_MS = 86_400_000;

/** Číslo dne (dny od 1. 1. 1970) z lokálního data. */
export function dayNumber(date: Date): number {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS);
}

/** Číslo dne z roku/měsíce/dne (měsíc 0-based, přetečení se normalizuje jako u `Date`). */
export function dayNumberOf(year: number, month: number, day: number): number {
  return Math.floor(Date.UTC(year, month, day) / DAY_MS);
}

/** `Date` (UTC půlnoc) odpovídající číslu dne — čti z něj jen `getUTC*`. */
export function dateOfDay(day: number): Date {
  return new Date(day * DAY_MS);
}

/** Kotva kalendáře — první den měsíce jako dvojice rok/měsíc (0-based). */
export interface MonthAnchor {
  year: number;
  month: number;
}

/** Posun kotvy o `delta` měsíců (i přes hranici roku). */
export function shiftMonth(anchor: MonthAnchor, delta: number): MonthAnchor {
  const raw = anchor.month + delta;
  return {
    year: anchor.year + Math.floor(raw / 12),
    month: ((raw % 12) + 12) % 12,
  };
}

/** Kotva měsíce, do kterého datum spadá. */
export function monthOf(date: Date): MonthAnchor {
  return { year: date.getFullYear(), month: date.getMonth() };
}

/** Kotva měsíce, do kterého spadá číslo dne. */
export function monthOfDay(day: number): MonthAnchor {
  const date = dateOfDay(day);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
}

/** Ordinální index měsíce (rok × 12 + měsíc) — porovnávání a klíčování měsíců. */
export function monthIndex(anchor: MonthAnchor): number {
  return anchor.year * 12 + anchor.month;
}

/**
 * Index dne v týdnu s pondělím na nule — a to i pro čísla dnů (1. 1. 1970 byl čtvrtek,
 * odtud posun o 3).
 */
export function weekdayOfDay(day: number): number {
  return ((day % 7) + 7 + 3) % 7;
}

/** Číslo dne pondělí toho týdne, do kterého `day` spadá. */
export function mondayOfDay(day: number): number {
  return day - weekdayOfDay(day);
}

/** Názvy měsíců v jazyce UI (leden…prosinec, s velkým počátečním písmenem). */
export function monthNames(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, index) => {
    const label = format.format(new Date(Date.UTC(2021, index, 1)));
    return label.charAt(0).toUpperCase() + label.slice(1);
  });
}
